import assert from 'assert';

import {tryParseAsMoment} from 'notebook/data-tables/DatetimeFields';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {momentToDaysSinceEpoch} from 'notebook/tables/excess/Util';
import {Table} from 'notebook/tables/TableConstants';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';

/**
 * An intermediate representation of a cell's value used only for sorting.
 */
interface CellSortValue {
  /**
   * Index of the cell's row in its table.
   */
  rowIndex: number;
  /**
   * Numeric value if the cell has a numeric or temporal typed value, else null.
   */
  numericValue: number | null;
  /**
   * Lowercase formatted value of cell.
   */
  stringValue: string;
  /**
   * Whether the cell is completely empty.
   */
  isEmpty: boolean;
  /**
   * Whether the cell has any error.
   */
  isError: boolean;
}

/**
 * Attempts to return the numeric value from a typed value. Otherwise, it returns null.
 */
function getNumericValue(typedValue: TypedValue | null): number | null {
  const dateValue =
    typedValue?.type === BaseTypedValueType.DATE
      ? typedValue.value
      : typedValue?.type === BaseTypedValueType.DATETIME
        ? typedValue.value
        : typedValue?.type === FieldType.DATE
          ? typedValue.dateValue
          : typedValue?.type === FieldType.DATETIME
            ? typedValue.datetimeValue
            : null;
  if (dateValue) {
    const moment = tryParseAsMoment(dateValue);
    if (moment) {
      return momentToDaysSinceEpoch(moment);
    }
  }

  return typedValue?.type === FieldType.INTEGER
    ? typedValue.integerValue
    : typedValue?.type === FieldType.FLOAT
      ? typedValue.floatValue
      : typedValue?.type === BaseTypedValueType.NUMBER
        ? typedValue.rawValue
        : typedValue?.type === BaseTypedValueType.SCIENTIFIC
          ? typedValue.rawValue
          : null;
}

/**
 * Returns the CellSortValue for the given cell in a table.
 */
function getRowValue(
  table: Table,
  computedTable: Array<Array<EvaluatedCell>> | undefined,
  {columnIndex, rowIndex}: {columnIndex: number; rowIndex: number}
): CellSortValue {
  const cell = table.table[rowIndex][columnIndex];
  const computedCell = computedTable?.[rowIndex]?.[columnIndex];
  const typedValue = computedCell?.value ?? cell.typedValue ?? null;
  const formattedValue = typedValue ? formatTypedValue(typedValue) : cell.text;
  return {
    rowIndex,
    numericValue: getNumericValue(typedValue),
    stringValue: formattedValue.toLocaleLowerCase(),
    // Differs from TableUtil.isCellEmpty() because we want to consider cells with blank typed values as empty for sorting.
    // A cell with the formula `=""` has an empty formattedValue but is not considered empty for sorting.
    // Google Sheets/Excel consider a cell that references an empty cell also empty, but we don't have a separate concept of an "null" cell vs a cell with an empty value.
    isEmpty: formattedValue === '' && cell.text === '',
    isError: computedCell?.error != null,
  };
}

/**
 * Compares cells case-insensitively and with natural sort.
 * Note that `compareCellSortValues(a, b) !== -compareCellSortValues(b, a)` if a is empty xor b is empty.
 *
 * @returns a negative value if `a` should come before `b` in the given sort direction, a positive value if `a` should come after `b`, and 0 if they are equal.
 */
function compareCellSortValues(a: CellSortValue, b: CellSortValue, reverse: boolean): number {
  const multiplier = reverse ? -1 : 1;

  // Always sort empty cells to the end regardless of sort direction
  if (a.isEmpty && b.isEmpty) {
    return 0;
  } else if (a.isEmpty) {
    return 1;
  } else if (b.isEmpty) {
    return -1;
  }

  // Always consider cells with error to be "larger" than non-errored cells
  if (a.isError && b.isError) {
    return 0; // All errors are equal
  } else if (a.isError) {
    return multiplier;
  } else if (b.isError) {
    return -multiplier;
  }

  if (a.numericValue != null && b.numericValue != null) {
    // Both numeric, so use numeric comparison
    return multiplier * (a.numericValue - b.numericValue);
  } else if (a.numericValue == null && b.numericValue != null) {
    /**
     * Consider any non-numeric value to be "larger" than any numeric value, like Excel.
     * If we didn't, we would end up with this inconsistent sort:
     * - "3" < "1e6" (numeric comparison)
     * - "1e6" < "2z" (alphabetic comparison)
     * - "2z" < "3" (alphabetic comparison) (transitivity violation because "3" < "3")
     * Our rule that "non-numeric is always larger" considers "3" < "2z" and maintains transitivity.
     */
    return multiplier;
  } else if (a.numericValue != null && b.numericValue == null) {
    return -multiplier;
  } else {
    // Both non-numeric, so use natural alphabetic comparison
    return multiplier * a.stringValue.localeCompare(b.stringValue, undefined, {numeric: true});
  }
}

interface ColumnSortAnalytics {
  numEmpty: number;
  numErrors: number;
  numRowsMoved: number;
  numLocked: number;
  wasSorted: boolean; // Whether the column was originally sorted ascending
  wasReverseSorted: boolean; // Whether the column was originally sorted descending
}

/**
 * Determines the sort order for a table's rows by sorting the row values by a given column.
 *
 * @returns sortedIndices: An array of indices {new index -> old index} where newRows[i] = oldRows[sortedIndices[i]]
 * @returns analyticsParams: Partial analytics data about the column that was sorted
 */
export default function sortTableByColumn(
  table: Table,
  computedTable: Array<Array<EvaluatedCell>> | undefined,
  columnIndex: number,
  options: {reverse: boolean} = {reverse: false}
): {sortedIndices: Array<number>; analyticsParams: ColumnSortAnalytics} {
  assert(
    columnIndex >= 0 && columnIndex < table.columnConstraints.length,
    `Invalid column index ${columnIndex}`
  );

  const cellSortValues = table.table.map((_, rowIndex) =>
    getRowValue(table, computedTable, {columnIndex, rowIndex})
  );
  const sortedIndices = [...cellSortValues] // Copy to avoid mutating original array
    .sort((a, b) => compareCellSortValues(a, b, options.reverse))
    .map(({rowIndex}) => rowIndex);

  const analyticsParams: ColumnSortAnalytics = {
    numEmpty: cellSortValues.filter((cell) => cell.isEmpty).length,
    numErrors: cellSortValues.filter((cell) => cell.isError).length,
    numRowsMoved: sortedIndices.filter((newIndex, oldIndex) => newIndex !== oldIndex).length,
    numLocked: table.table.filter((row) => row[columnIndex].locked).length,
    wasSorted: cellSortValues.every(
      (_, index) =>
        index === 0 || compareCellSortValues(cellSortValues[index - 1], cellSortValues[index], false) <= 0
    ),
    wasReverseSorted: cellSortValues.every(
      (_, index) =>
        index === 0 || compareCellSortValues(cellSortValues[index - 1], cellSortValues[index], true) <= 0
    ),
  };

  return {sortedIndices, analyticsParams};
}
